/**
 * Shared vocabulary for the analytics panels — ported from analytics-shared.tsx.
 *
 * Everything here exists because two panels would otherwise derive the same fact
 * two ways. The notes rules in particular are load-bearing: they are what keep
 * the screen from printing the same sentence five times, and what keep it from
 * dropping a sentence that changes what a number means.
 */
import {
  AnalyticsBatchOk,
  AnalyticsBatchResult,
  AnalyticsItem,
  isBatchFailure,
} from '../../../../../api/analytics-client';
import { TableRow } from '../charts/chart-table-view';
import { format } from '../charts/chart-theme';
import { zoneLabel } from '../../../../../function-utils/building-time';
import { ChartShellState } from '../charts/chart-shell';

/**
 * The label for a group whose actor could not be matched to a name in this
 * organisation. The server sends "(name unavailable)" for a staff id it could
 * not resolve and "(not attributed to a person)" for a row with no actor at all,
 * and both are correct sentences — but neither is a blank, and neither must be
 * rendered as one. They come through unchanged; this is only how the renderer
 * RECOGNISES them, so it can give them the neutral bucket token instead of a
 * series colour rather than paint a bucket as if it were a person.
 */
export const UNMATCHED_LABELS = [
  '(name unavailable)',
  '(not attributed to a person)',
];

export const isUnmatched = (item: AnalyticsItem): boolean =>
  UNMATCHED_LABELS.includes(item.label) || item.key === '';

export const asNumber = (item: AnalyticsItem): number =>
  format.numberOf(item.value) ?? 0;

/**
 * THE CROSS-ZONE SENTENCE, ONCE.
 *
 * Every analytics answer is on ONE clock now (R-T12): one window and one set of
 * buckets for every building, whatever zones the buildings are in. The server
 * says so itself in `notes` whenever an answer spans zones ("Every figure here
 * is on one clock, …"), and the page caption says it once for the screen.
 *
 * So this adds a sentence ONLY when the answer spans zones and the server's own
 * notes do not already carry one — never a second copy beside the first.
 *
 * It used to choose between two sentences, "each building's period was measured
 * in its OWN zone" and "measured in ONE window, so a building's own day did not
 * begin at that instant". The first described per-building windows that no
 * longer exist; the second apologised for what is now the rule.
 */
const SAYS_ONE_CLOCK = /one clock/i;

export const zoneCaption = (zones: string[], notes: string[] = []): string | null => {
  const names = Array.from(new Set((zones || []).map(zone => zoneLabel(zone) || zone)));
  if (names.length < 2) return null;
  if (notes.some(note => SAYS_ONE_CLOCK.test(note))) return null;

  return (
    `These buildings are in ${names.length} time zones (${names.join(', ')}); every ` +
    'figure here is on one clock, the same day for every building.'
  );
};

/**
 * THE NOTES A HUB PANEL SHOULD ACTUALLY PRINT.
 *
 * Every sentence the server sends is load-bearing and none is dropped for being
 * inconvenient — but two of them are already on this screen, prominently, and
 * printing them again once per panel is how a page ends up saying the same thing
 * five times. The first browser pass on the analytics screen showed exactly
 * that.
 *
 *  · `scope.note` is appended to each of the four hub responses by the route. It
 *    is ALSO what drives the page-level enforcement alert. Rendered in both
 *    places it appeared five times in one scroll. It is dropped HERE, never from
 *    the page — the alert is the louder and better placement, and this is a
 *    de-duplication rather than a suppression.
 *
 *  · NO cross-zone caption is added here. The top bar's caption already says,
 *    once, that an answer spanning several zones is on one clock; the per-panel
 *    copy existed for the per-building case, which no longer occurs
 *    (`perBuildingWindows` is always false since Plan B).
 */
export const hubNotes = (
  data: {
    notes: string[];
    scope: { note: string };
    zones: string[];
    perBuildingWindows: boolean;
  },
  extra: string[] = []
): string[] => {
  const pageNote = data.scope?.note || '';
  const out = (data.notes || []).filter(note => !pageNote || note !== pageNote);
  out.push(...extra);

  return out;
};

/** The notes a BATCH SLOT prints: the server's own, plus the renderer's, plus
 *  the cross-zone caption, plus the truncation sentence. */
export const slotNotes = (
  data: AnalyticsBatchOk,
  extra: string[] = []
): string[] => {
  const notes = [...(data.notes || []), ...extra];
  const zoneNote = zoneCaption(data.zones || [], notes);
  if (zoneNote) notes.push(zoneNote);
  if (data.truncated) {
    notes.push(
      `Only the first ${data.items.length} of ${data.groups} groups are shown; ` +
        'the total above still counts every one of them.'
    );
  }

  return notes;
};

/** Does this panel have anything worth drawing?
 *
 *  A zero-filled dimension always returns rows, so "no items" and "every item is
 *  zero" are DIFFERENT states and only the first is empty — a building league of
 *  all zeros is a real answer and the most interesting one on the screen. */
export const hasAnyItems = (data: AnalyticsBatchOk): boolean =>
  Array.isArray(data.items) && data.items.length > 0;

/** items -> table rows. ONE conversion, so the table, the CSV export and the
 *  chart can never disagree about what a row says. */
export const rowsFromItems = (
  data: AnalyticsBatchOk,
  labelOf: (item: AnalyticsItem) => string = item => item.label,
  /** The clock a `max_at` instant is printed on. The answer's own
   *  (`clockZone`) by default — it was a hardcoded 'UTC', silently, under a
   *  caption naming another zone. */
  tz: string = data.clockZone || ''
): TableRow[] =>
  data.items.map(item => ({
    key: item.key || item.label,
    label: labelOf(item),
    value:
      data.agg === 'max_at'
        ? format.instant(typeof item.value === 'string' ? item.value : null, tz)
        : format.count(format.numberOf(item.value)),
    compareValue: format.count(item.compareValue),
    delta: format.delta(item.delta),
  }));

/**
 * A panel's own failure, folded into the shell's state.
 *
 * WITHOUT THIS the isolation `readSlot` computes is invisible: the slot knows it
 * failed, the shell renders a chart of nothing, and the panel says "no data" for
 * a request that ERRORED. "Nothing happened" and "we could not find out" are
 * different facts and only one of them is true.
 *
 * A slot that is still in flight is NOT an error — while the batch is loading
 * every slot is legitimately missing, and `readSlot` cannot tell "not answered
 * yet" from "answered without this panel". The shell's own `loading` can, so it
 * wins.
 */
export const slotState = (state: ChartShellState, slot: SlotView): ChartShellState =>
  state.loading || state.error || !slot.error ? state : { ...state, error: slot.error };

/**
 * COMPARISON MODE, FOLDED INTO THE SLOT EVERY PANEL ALREADY DRAWS.
 *
 * Each panel already renders `compareValue` as a muted second series, because
 * "vs previous period" needed one. The two comparisons are mutually exclusive
 * (filter-reducer invariant 9d), so the second SCOPE can occupy the same slot:
 * one series, one axis, one legend entry — and every panel supports it without
 * a line of its own.
 *
 * The alternative was a second chart per panel, which the plan rejected for the
 * reason a second chart always loses: two y-axes scaled independently make a
 * building with a fifth of the traffic look identical to one with all of it.
 *
 * ── MATCHED BY KEY, NEVER BY POSITION ──
 *
 * The two scopes come back from the SAME plan, but not necessarily with the same
 * rows: a kind recorded in one building and not the other is present in one list
 * and absent from the other, and the lists are ordered by value. Zipping them by
 * index would attribute one building's Deliveries to the other's Guests — a
 * chart that is wrong rather than incomplete, and wrong in a way nobody can see.
 *
 * A key present only in the comparison scope is APPENDED with a primary value of
 * zero, because "that building had 40 and this one had none" is exactly the
 * finding a reader turns comparison mode on to make.
 */
export const mergeCompareScope = (
  primary: AnalyticsBatchResult | undefined,
  other: AnalyticsBatchResult | undefined
): AnalyticsBatchResult | undefined => {
  if (!primary || isBatchFailure(primary)) return primary;
  if (!other || isBatchFailure(other)) {
    // The second scope has not landed, or failed on its own. The primary is not
    // less true for it — it renders alone, with no comparison series, rather
    // than waiting.
    return { ...primary, items: primary.items.map(item => ({ ...item, compareValue: null, delta: null })), compareTotal: null, delta: null };
  }

  const byKey = new Map(other.items.map(item => [item.key || item.label, item]));

  const items = primary.items.map(item => {
    const match = byKey.get(item.key || item.label);
    byKey.delete(item.key || item.label);
    const mine = format.numberOf(item.value) ?? 0;
    const theirs = match ? format.numberOf(match.value) ?? 0 : null;

    return {
      ...item,
      compareValue: theirs,
      delta: theirs == null ? null : mine - theirs,
    };
  });

  for (const [, item] of byKey) {
    const theirs = format.numberOf(item.value) ?? 0;
    items.push({ ...item, value: 0, compareValue: theirs, delta: -theirs });
  }

  return {
    ...primary,
    items,
    compareTotal: typeof other.total === 'number' ? other.total : null,
    delta:
      typeof primary.total === 'number' && typeof other.total === 'number'
        ? primary.total - other.total
        : null,
    /* THE TWO WINDOWS ARE THE SAME WINDOW — it is the PLACE that differs — so a
       percentage IS valid here even when a period-over-period one would not be. */
    likeForLike: true,
  };
};

/** A batch slot resolved into the four things a panel shell needs. */
export interface SlotView {
  ok?: AnalyticsBatchOk;
  error?: string;
  empty: boolean;
  notes: string[];
}

export const readSlot = (
  result: AnalyticsBatchResult | undefined,
  extraNotes: string[] = []
): SlotView => {
  if (!result) {
    // NOT a skeleton. The batch echoes one slot per panel it was asked for, so a
    // missing slot means the request and the screen disagree about what was
    // asked — and a skeleton that never resolves reads as a slow network for as
    // long as anybody is willing to wait.
    return {
      error:
        'The server answered without a result for this panel, so there is nothing to show and nothing to infer.',
      empty: false,
      notes: [],
    };
  }
  if (isBatchFailure(result)) {
    // ISOLATED. This slot says why it could not answer; its siblings still
    // render their numbers.
    return { error: result.error, empty: false, notes: [] };
  }

  return {
    ok: result,
    empty: !hasAnyItems(result),
    notes: slotNotes(result, extraNotes),
  };
};

/** The dimension-substitution caveat.
 *
 *  A panel whose title says one thing while its bars say another is a chart with
 *  the wrong title, and that has to be visible where the title is — not only in
 *  the notes underneath. This screen never requests an illegal pair, so in
 *  practice it fires only if the measure registry changes under a bookmarked
 *  URL. */
export const substitutionCaveat = (data?: AnalyticsBatchOk): string | undefined =>
  data && data.dimension !== data.requestedDimension
    ? `Grouped by ${format.humanise(data.dimension).toLowerCase()}, not by the ` +
      `${format.humanise(data.requestedDimension).toLowerCase()} that was requested — ` +
      'this measure has no meaningful split by it.'
    : undefined;
